<script lang="ts">
import type {JsonValue} from 'type-fest';
import EcadInspector from './EcadInspector.vue';
import {onNextFrame} from '../domutils.ts';
import {event} from '../analytics.ts';
import {defineComponent} from 'vue';
import type {InspectorContext, TreeNode} from './inspector_tree.ts';

const VIEWPORT_PADDING = 8;

export type EcadInspectorFrameData = {
  show: boolean,
  clientX: number | null,
  clientY: number | null,
  _constraintBounds: {minLeft: number, maxRight: number, minTop: number, maxBottom: number} | null,
  _resizeObserver: ResizeObserver | null,
};

/**
 * Events:
 *
 * - inspector-did-close: emitted after the frame closed
 */
export default defineComponent({
  name: 'EcadInspectorFrame',
  components: {
    EcadInspector,
  },
  props: {
    isDiff: {
      type: Boolean,
      required: true,
    },
  },

  emits: ['inspector-did-close'],

  data(): EcadInspectorFrameData {
    return {
      show: false,
      clientX: null,
      clientY: null,
      // eslint-disable-next-line vue/no-reserved-keys
      _constraintBounds: null,
      // eslint-disable-next-line vue/no-reserved-keys
      _resizeObserver: null,
    };
  },

  beforeUnmount() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  },

  methods: {
    _updateConstraintBounds() {
      const el = (this.$refs.inspectorHeader as HTMLElement);
      if (!el?.offsetParent) return;
      const offsetParent = el.offsetParent;
      const parentRect = offsetParent.getBoundingClientRect();
      const constraintEl = offsetParent.querySelector('.selectable-region');
      const constraintRect = constraintEl ? constraintEl.getBoundingClientRect() : {
        left: VIEWPORT_PADDING,
        right: window.innerWidth - VIEWPORT_PADDING,
        top: VIEWPORT_PADDING,
        bottom: window.innerHeight - VIEWPORT_PADDING,
      };
      this._constraintBounds = {
        minLeft: (constraintRect.left + VIEWPORT_PADDING) - parentRect.left,
        maxRight: (constraintRect.right - VIEWPORT_PADDING) - parentRect.left,
        minTop: (constraintRect.top + VIEWPORT_PADDING) - parentRect.top,
        maxBottom: (constraintRect.bottom - VIEWPORT_PADDING) - parentRect.top,
      };
    },

    _ensureConstraintObserver() {
      if (this._resizeObserver) return;
      const el = (this.$refs.inspectorHeader as HTMLElement);
      if (!el?.offsetParent) return;
      const offsetParent = el.offsetParent;
      const constraintEl = offsetParent.querySelector('.selectable-region');

      this._resizeObserver = new ResizeObserver(() => {
        this._updateConstraintBounds();
      });
      if (constraintEl) {
        this._resizeObserver.observe(constraintEl);
      }
      this._resizeObserver.observe(offsetParent);
    },

    /**
     * Set new inspector data when an element is clicked
     * @param {{clientX: number, clientY: number}} location
     * @param {Element} parent
     * @param {object | number | string | null} headContents
     * @param {object | number | string | null} baseContents
     * @param {string} type
     * @param {string} variant
     * @param {string} key
     * @param {string} units
     * @param {InspectorContext} metadataCtx
     * @param {TreeNode | null} prebuiltTree
     * @returns {Promise<void>}
     */
    setInspectorData(location: {clientX: number, clientY: number}, parent: Element, headContents: JsonValue, baseContents: JsonValue, type: string, variant: string, key: string, units: string, metadataCtx: InspectorContext, prebuiltTree: TreeNode | null = null) {
      return new Promise<void>((resolve, _reject) => {
        onNextFrame(() => {
          const inspectorContents = (this.$refs.inspectorContents as typeof EcadInspector);

          inspectorContents.repopulate(
            headContents,
            baseContents,
            type,
            variant,
            key,
            this.isDiff,
            units,
            metadataCtx,
            prebuiltTree,
          );

          // update the popup based on the new data
          const inspectorHeader = (this.$refs.inspectorHeader as HTMLElement);

          const parentBounds = parent.getBoundingClientRect();
          const parentCenterX = (parentBounds.width / 2) + parentBounds.left;
          const parentCenterY = (parentBounds.height / 2) + parentBounds.top;

          if (location.clientX < parentCenterX) {
            inspectorHeader.style.right = 'auto';
            inspectorHeader.style.left = `${location.clientX - parentBounds.left}px`;
          } else {
            inspectorHeader.style.right = `${parentBounds.right - location.clientX}px`;
            inspectorHeader.style.left = 'auto';
          }

          if (location.clientY < parentCenterY) {
            inspectorHeader.style.top = `${location.clientY - parentBounds.top}px`;
            inspectorHeader.style.bottom = 'auto';
          } else {
            inspectorHeader.style.top = 'auto';
            inspectorHeader.style.bottom = `${parentBounds.bottom - location.clientY}px`;
          }

          this.showInspector(true);
          requestAnimationFrame(() => {
            this.clampToViewport();
            resolve();
          });
        });
      });
    },

    clampToViewport() {
      this._ensureConstraintObserver();
      if (!this._constraintBounds) {
        this._updateConstraintBounds();
      }

      const el = (this.$refs.inspectorHeader as HTMLElement);
      const bounds = this._constraintBounds;
      if (!bounds) return;
      const top = el.offsetTop;
      const left = el.offsetLeft;
      el.style.right = 'auto';
      el.style.bottom = 'auto';

      el.style.left = `${Math.max(bounds.minLeft, Math.min(bounds.maxRight - el.offsetWidth, left))}px`;
      el.style.top = `${Math.max(bounds.minTop, Math.min(bounds.maxBottom - el.offsetHeight, top))}px`;
    },

    /**
     * Inspector window dragged
     * @param {PointerEvent} event
     */
    inspectorDragPointerDown(event: PointerEvent) {
      event.preventDefault();
      this.clientX = event.clientX;
      this.clientY = event.clientY;
      const inspectorHeader = (this.$refs.inspectorHeader as HTMLElement);
      inspectorHeader.addEventListener('pointermove', this.inspectorDrag);
      inspectorHeader.addEventListener('pointerup', this.inspectorDragClose);
      inspectorHeader.setPointerCapture(event.pointerId);
    },

    /**
     * Drag the inspector window
     * @param {PointerEvent} event
     */
    inspectorDrag(event: PointerEvent) {
      event.preventDefault();
      const inspectorHeader = (this.$refs.inspectorHeader as HTMLElement);
      inspectorHeader.style.top = `${(inspectorHeader.offsetTop - (this.clientY - event.clientY))}px`;
      inspectorHeader.style.left = `${(inspectorHeader.offsetLeft - (this.clientX - event.clientX))}px`;
      this.clampToViewport();
      this.clientX = event.clientX;
      this.clientY = event.clientY;
    },

    /**
     * Disable inspector drag
     * @param {PointerEvent} event
     */
    inspectorDragClose(event: PointerEvent) {
      const inspectorHeader = (this.$refs.inspectorHeader as HTMLElement);
      inspectorHeader.removeEventListener('pointermove', this.inspectorDrag);
      inspectorHeader.removeEventListener('pointerup', this.inspectorDragClose);
      inspectorHeader.releasePointerCapture(event.pointerId);
    },

    /**
     * Open or close the inspector window
     * @param {boolean} show
     */
    showInspector(show: boolean) {
      const previousShow = this.show;
      if (show && show !== previousShow) {
        event('inspector_opened');
      }
      // toggle the popup
      this.show = show;

      if (!show && previousShow) {
        this.$emit('inspector-did-close');
      }
    },

    /**
     * Inspector popup closed
     * @param {Event} event
     */
    onInspectorClose(event: Event) {
      event.preventDefault();
      this.showInspector(false);
    },
  },
});
</script>

<template>
  <div v-show="show" ref="inspectorHeader" class="inspector-header" style="position: absolute;">
    <div class="inspector-container">
      <div class="inspector-drag-bar" @pointerdown="inspectorDragPointerDown">
        <svg viewBox="0 0 15 15" class="svg octicon-grabber" width="25" height="25" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm-4 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm1-5a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm1-5a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM6 5a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
        </svg>
      </div>
      <div ref="inspectorWrapper" class="inspector-wrapper">
        <ecad-inspector ref="inspectorContents" @inspector-close="onInspectorClose"/>
      </div>
    </div>
  </div>
</template>

<style scoped>
.inspector-header {
  z-index: 1000;
  background: var(--color-box-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  box-shadow: 0 .5rem 1rem var(--color-shadow);
  max-width: 500px;
}

.inspector-wrapper {
  cursor: default;
  padding: 5px;
}

.inspector-container {
  display: flex;
  flex-direction: row;
}

.inspector-drag-bar {
  touch-action: none;
  cursor: move;
  user-select: none;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 5px;
}
</style>
